import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Visitors from './pages/Visitors';
import Employees from './pages/Employees';
import Meetings from './pages/Meetings';
import GuestRooms from './pages/GuestRooms';
import GuestHouses from './pages/GuestHouses';
import Expenses from './pages/Expenses';
import Users from './pages/Users';
import Reports from './pages/Reports';
import Alerts from './pages/Alerts';
import Companies from './pages/Companies';
import Units from './pages/Units';
import Vehicles from './pages/Vehicles';
import Drivers from './pages/Drivers';
import VehicleTrips from './pages/VehicleTrips';
import FuelExpenses from './pages/FuelExpenses';
import ExternalCourier from './pages/ExternalCourier';
import InternalCourier from './pages/InternalCourier';
import CourierCompanies from './pages/CourierCompanies';
import HotelBookings from './pages/HotelBookings';
import BackupRestore from './pages/BackupRestore';
import AdminHelp from './pages/AdminHelp';
import MeetingRooms from './pages/MeetingRooms';
import AddressBook from './pages/AddressBook';
import AccessManagement from './pages/AccessManagement';
import GoogleDriveBackup from './pages/GoogleDriveBackup';
import UserGuide from './pages/UserGuide';
import ShowroomSetup from './pages/ShowroomSetup';
import EmployeeVehicles from './pages/EmployeeVehicles';

const PrivateRoute = ({ children, adminOnly, adminOrManager }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  if (adminOrManager && !['admin', 'manager'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="visitors" element={<Visitors />} />
        <Route path="employees" element={<Employees />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="rooms" element={<GuestRooms />} />
        <Route path="guest-houses" element={<GuestHouses />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="users" element={<PrivateRoute adminOrManager><Users /></PrivateRoute>} />
        <Route path="reports" element={<Reports />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="companies" element={<PrivateRoute adminOnly><Companies /></PrivateRoute>} />
        <Route path="units" element={<PrivateRoute adminOnly><Units /></PrivateRoute>} />
        <Route path="vehicles" element={<Vehicles />} />
        <Route path="drivers" element={<Drivers />} />
        <Route path="trips" element={<VehicleTrips />} />
        <Route path="fuel" element={<FuelExpenses />} />
        <Route path="couriers" element={<ExternalCourier />} />
        <Route path="internal-courier" element={<InternalCourier />} />
        <Route path="courier-companies" element={<PrivateRoute adminOnly><CourierCompanies /></PrivateRoute>} />
        <Route path="hotel-bookings" element={<HotelBookings />} />
        <Route path="backup" element={<PrivateRoute adminOnly><BackupRestore /></PrivateRoute>} />
        <Route path="admin-help" element={<PrivateRoute adminOnly><AdminHelp /></PrivateRoute>} />
        <Route path="meeting-rooms" element={<MeetingRooms />} />
        <Route path="address-book" element={<AddressBook />} />
        <Route path="access-management" element={<PrivateRoute adminOnly><AccessManagement /></PrivateRoute>} />
        <Route path="drive-backup" element={<PrivateRoute adminOnly><GoogleDriveBackup /></PrivateRoute>} />
        <Route path="user-guide" element={<UserGuide />} />
        <Route path="employee-vehicles" element={<EmployeeVehicles />} />
        <Route path="showroom-setup" element={<ShowroomSetup />} />
      </Route>
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
